import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import set from 'ember-set-helper/helpers/set';
import type { RecurringOptions } from 'garaje/components/invites/select-recurring-invites';
import SelectRecurringInvites from 'garaje/components/invites/select-recurring-invites';
import SendNotificationModal from 'garaje/components/invites/send-notification-modal';
import WorkflowReevaluationWarning from 'garaje/components/invites/workflow-reevaluation-warning';
import ButtonGroup from 'garaje/components/ui/button-group';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type Invite from 'garaje/models/invite';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import { RECURRING_OPTIONS } from 'garaje/utils/enums';
import { reads } from 'macro-decorators';
import type { ValueOf } from 'type-fest';

interface EditRecurringInvitesModalSignature {
  Args: {
    close: () => void;
    disabled?: RecurringOptions;
    invite?: Invite;
    isDenied?: boolean;
    modelName?: string;
    onSubmit: (scope: ValueOf<typeof RECURRING_OPTIONS>, reason?: string | null) => Promise<void>;
    options?: RecurringOptions;
    scope: ValueOf<typeof RECURRING_OPTIONS>;
    shouldSendNotification?: boolean;
    showReevaluationWarning?: boolean;
  };
}

export default class EditRecurringInvitesModal extends Component<EditRecurringInvitesModalSignature> {
  @service declare featureFlags: FeatureFlagsService;

  @tracked sendNotificationCallback: (() => Promise<void>) | undefined;
  @tracked scope = this.args.scope || RECURRING_OPTIONS.THIS;
  @tracked reason = '';

  @reads('args.modelName', 'invite') modelName!: string;

  @action
  onSelectScope(scope: ValueOf<typeof RECURRING_OPTIONS>): void {
    this.scope = scope;
  }

  updateReason = (event: Event): void => {
    this.reason = (event.target as HTMLTextAreaElement).value;
  };

  onSubmitTask = dropTask(async (e: Event) => {
    e.preventDefault();
    // undefined when no warning is shown, so the save omits meta.reason entirely.
    const reason = this.args.showReevaluationWarning ? this.reason.trim() || null : undefined;
    if (this.args.shouldSendNotification) {
      this.sendNotificationCallback = () => this.args.onSubmit(this.scope, reason);
    } else {
      await this.args.onSubmit(this.scope, reason);
      this.args.close();
    }
  });

  <template>
    {{#if this.sendNotificationCallback}}
      <SendNotificationModal
        @callback={{this.sendNotificationCallback}}
        @onClickBack={{set this "sendNotificationCallback" undefined}}
        @close={{@close}}
        @invites={{array @invite}}
        @action="update"
        @scope={{this.scope}}
        @prompt="Would you like to send an update email to your guest?"
      />
    {{else}}
      <ModalDialog @clickOutsideToClose={{true}} @onClickOverlay={{@close}} @translucentOverlay={{true}}>
        <FocusTrapper>
          <div class="p-16" data-test-edit-recurring-invites-modal>
            <h1>Edit recurring {{this.modelName}}</h1>
            <form {{on "submit" this.onSubmitTask.perform}}>
              <SelectRecurringInvites
                @scope={{this.scope}}
                @disabled={{@disabled}}
                @modelName={{this.modelName}}
                @onChange={{this.onSelectScope}}
                @onSubmitTask={{this.onSubmitTask}}
                @options={{@options}}
              />
              {{#if @showReevaluationWarning}}
                <div class="pt-4">
                  <WorkflowReevaluationWarning
                    @isDenied={{@isDenied}}
                    @reason={{this.reason}}
                    @onReasonInput={{this.updateReason}}
                  />
                </div>
              {{/if}}
              <ButtonGroup @reverse={{true}} class="pt-8 flex justify-end">
                <button
                  class="btn btn--small btn--brand {{if this.onSubmitTask.isRunning 'disabled'}}"
                  disabled={{this.onSubmitTask.isRunning}}
                  type="submit"
                  data-test-edit-recurring-invite-button
                >
                  {{#if this.onSubmitTask.isRunning}}
                    Saving...
                  {{else}}
                    Save
                  {{/if}}
                </button>
                <button
                  class="btn btn--small btn--hoverFill {{if this.onSubmitTask.isRunning 'disabled'}}"
                  disabled={{this.onSubmitTask.isRunning}}
                  {{on "click" @close}}
                  type="button"
                >
                  Cancel
                </button>
              </ButtonGroup>
            </form>
          </div>
        </FocusTrapper>
      </ModalDialog>
    {{/if}}
  </template>
}
